// MIDAS V3 — UI state slice (Phase 1 scaffold)
//
// Source spec: IMPLEMENTATION_TEMPLATES/ToDo/MidasChat/MIDAS_V3_PHASE1_SCAFFOLD_SPEC.md §5
// Master design: IMPLEMENTATION_TEMPLATES/ToDo/MidasChat/MIDAS_V3_MASTER_DESIGN.md §5
//
// v3 introduces its own slice (`midasV3`) so that no v2 slice is mutated by v3
// work. v3 reads `chatBotSlice` (machine context) and `midasHistoryPageSlice`
// (threads) verbatim, and only adds UI state that v2 doesn't track.
//
// Phase 1 wires: theme (claude | machineid, persisted to localStorage),
// railCollapsed (persisted), and stub fields for the artifact dock (Phase 3).
//
// No MUI, no @emotion, no `sx`, no `dt` tokens — see CLAUDE.md MIDAS-V3-P1 spawn rules.
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// ============================================================
// Types
// ============================================================

export type MidasV3Theme = "claude" | "machineid";

export interface MidasV3State {
  /** Active palette class — `.p-claude` (default) or `.p-machineid`. */
  theme: MidasV3Theme;
  /** Whether the history rail is collapsed (56px) vs expanded (248px). */
  railCollapsed: boolean;
  /** Width of the artifact dock when one is open (Phase 3 will read this). */
  artifactDockWidth: number;
  /** Id of the artifact currently rendered fullscreen, or null. */
  fullscreenArtifactId: string | null;
  /** Per-artifact-id overlay visibility map. Default visible = true. */
  overlayVisibility: Record<string, boolean>;
}

// ============================================================
// localStorage hydration
// ============================================================

const LS_THEME_KEY = "midas-v3-theme";
const LS_RAIL_COLLAPSED_KEY = "midas-v3-rail-collapsed";

const safeLocalStorage = (): Storage | null => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
};

const readTheme = (): MidasV3Theme => {
  const ls = safeLocalStorage();
  if (!ls) return "claude";
  const raw = ls.getItem(LS_THEME_KEY);
  if (raw === "claude" || raw === "machineid") return raw;
  return "claude";
};

const writeTheme = (theme: MidasV3Theme): void => {
  const ls = safeLocalStorage();
  if (!ls) return;
  try {
    ls.setItem(LS_THEME_KEY, theme);
  } catch {
    // localStorage may throw in private mode / quota; not fatal for v3 UX.
  }
};

const readRailCollapsed = (): boolean => {
  const ls = safeLocalStorage();
  if (!ls) return false;
  const raw = ls.getItem(LS_RAIL_COLLAPSED_KEY);
  return raw === "1";
};

const writeRailCollapsed = (collapsed: boolean): void => {
  const ls = safeLocalStorage();
  if (!ls) return;
  try {
    ls.setItem(LS_RAIL_COLLAPSED_KEY, collapsed ? "1" : "0");
  } catch {
    // ignore
  }
};

// ============================================================
// Initial state
// ============================================================

const initialState: MidasV3State = {
  theme: readTheme(),
  railCollapsed: readRailCollapsed(),
  artifactDockWidth: 620,
  fullscreenArtifactId: null,
  overlayVisibility: {},
};

// ============================================================
// Slice
// ============================================================

const midasV3Slice = createSlice({
  name: "midasV3",
  initialState,
  reducers: {
    setMidasV3Theme: (state, action: PayloadAction<MidasV3Theme>) => {
      state.theme = action.payload;
      writeTheme(action.payload);
    },
    toggleRailCollapsed: (state) => {
      state.railCollapsed = !state.railCollapsed;
      writeRailCollapsed(state.railCollapsed);
    },
    setRailCollapsed: (state, action: PayloadAction<boolean>) => {
      state.railCollapsed = action.payload;
      writeRailCollapsed(action.payload);
    },
    // ── Artifact dock stubs (Phase 3 wires these — included now so the
    //    slice shape is stable across phases and selectors don't need to
    //    branch on field presence). ──
    setArtifactDockWidth: (state, action: PayloadAction<number>) => {
      state.artifactDockWidth = action.payload;
    },
    setFullscreenArtifactId: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.fullscreenArtifactId = action.payload;
    },
    setOverlayVisibility: (
      state,
      action: PayloadAction<{ artifactId: string; visible: boolean }>
    ) => {
      state.overlayVisibility[action.payload.artifactId] =
        action.payload.visible;
    },
  },
});

export const {
  setMidasV3Theme,
  toggleRailCollapsed,
  setRailCollapsed,
  setArtifactDockWidth,
  setFullscreenArtifactId,
  setOverlayVisibility,
} = midasV3Slice.actions;

export default midasV3Slice.reducer;
